import React, { lazy, useEffect, useState } from 'react'
import { Redirect, Route, Switch, useHistory, useLocation } from 'react-router-dom'

import { JSBoothDetail } from '../job-seeker/components/JSBoothDetail'
import { RecruiterBoothView } from './components/RecruiterBoothView'
import RecruiterHistory from './components/RecruiterHistory'
import ViewResume from './components/ViewResume';
const RecruiterInterview = lazy(() => import('./components/RecruiterInterview'));

import { AppModal } from '../shared/modals/AppModal'
import { useAppContext } from 'app/context/app-context'
import { InterviewSession, EndReason, VideoSessionStatus } from 'app/API'
import { InterviewSessionService } from 'app/services/InterviewSessionService'
import { QueueUserService } from 'app/services/QueueUserService'
import { useAuth } from 'app/context/auth-context'
import { recruiterUrl } from 'app/types'
import { setupAmplifyMessageSubscription } from 'app/amplify-subscriptions';
import { UrlParamReplace } from 'app/helpers/MiscUtils'
import useIsMounted from 'app/hooks/useIsMounted'
import { HMSRoomProvider } from '@100mslive/react-sdk'
import { RxSubscription } from 'app/RxSubscriptions'
import { ManageProfile } from '../shared/account/manage-profile'

export const RecruiterRoutes = {
  Booth: `${recruiterUrl}/booth`,
  Interview: `${recruiterUrl}/interview/:sessionId`,
  ManageBoothInfo: `${recruiterUrl}/manage-booth-info/:boothId`,
  DeviceTest: `${recruiterUrl}/device-test`,
  BoothPreview: `${recruiterUrl}/booth-preview/:boothId`,
  History: `${recruiterUrl}/history`,
  WaitingLobby: `${recruiterUrl}/waiting/:boothId`,
  Profile: `${recruiterUrl}/profile`,
  ViewResume: `${recruiterUrl}/view-resume`,
}

const recruiterBreadCrumbs = [{
  text: 'Booth',
  url: UrlParamReplace(RecruiterRoutes.Booth, '', '')
}
]

interface RecruiterPageHOCProps {
  children: React.ReactNode;
  withContinuePopup?: boolean;
}

const RecruiterPageHOC = (hocProps: RecruiterPageHOCProps) => {

  const interviewSessionService = new InterviewSessionService();
  const { user } = useAuth();
  const { jobFair } = useAppContext();
  const [activeInterviewSession, setActiveInterviewSession] = useState<InterviewSession | undefined>(undefined);
  const history = useHistory();
  const location = useLocation();
  const queueUserService = new QueueUserService();


  const isMounted = useIsMounted('RecruiterPageHOC');

  useEffect(() => {
    if (!user || !jobFair?.id)
      return;

    const messageSubs = setupAmplifyMessageSubscription(`${jobFair.id}_Recruiter`);

    return () => {
      messageSubs?.dispose();
    }
  }, [user, jobFair?.id])

  useEffect(() => {

    if (!jobFair?.id || !user?.id)
      return;


    const subscription = RxSubscription.onChangeInterviewSessionByRecruiterIdObservable().subscribe(changeEventInterviewSession => {
      if (!isMounted()) return;
      if (changeEventInterviewSession?.mode === 'update' && changeEventInterviewSession.value?.status === VideoSessionStatus.IN_PROGRESS) {
        setActiveInterviewSession(changeEventInterviewSession.value);
      }
      else if (changeEventInterviewSession?.mode === 'update' && (changeEventInterviewSession.value?.status === VideoSessionStatus.ENDING || changeEventInterviewSession.value?.status === VideoSessionStatus.ENDED)) {
        setActiveInterviewSession(undefined);
      }
    });

    const getActive = async () => {
      if (!isMounted())
        return;
      setActiveInterviewSession(undefined);
      await interviewSessionService.getActiveInterviewsByRecruiter(jobFair.id, user.id).then(interviewSessions => {
        if (!isMounted()) return;
        if (interviewSessions?.length) {
          setActiveInterviewSession(interviewSessions[0]);
          //ActiveInterviewSessionState.setInterviewSession(interviewSessions[0]);
        }
      });
    }
    getActive();

    return () => {
      subscription.unsubscribe();
    }

  }, [jobFair, user]);

  return (
    <HMSRoomProvider>
      {hocProps.withContinuePopup !== false && <AppModal variant="PlainText" successText="Continue session" rejectText="End Session" show={!!activeInterviewSession && activeInterviewSession.status === VideoSessionStatus.IN_PROGRESS && location.pathname !== UrlParamReplace(RecruiterRoutes.Interview, ':sessionId', activeInterviewSession?.id || '')} title="Alert"
        onSuccess={() => {
          if (activeInterviewSession) {
            history.push(UrlParamReplace(RecruiterRoutes.Interview + '?startVideo=true&startAudio=true', ':sessionId', activeInterviewSession.id));
          }
          setActiveInterviewSession(undefined);
        }}

        onReject={async () => {

          // Pull user from selected booth
          if (jobFair?.id && activeInterviewSession && activeInterviewSession.boothId && user) {
            try {
              const interviewSession = await queueUserService.endInterview(jobFair.id, activeInterviewSession.boothId, activeInterviewSession.id, user.id, EndReason.ENDED);
              if (interviewSession) {
                setActiveInterviewSession(undefined);
              }
            } catch (err) {
              console.log('Could not pull user, please try again')
            }
          }
        }}
      >
        You are still in an interview, please click "Continue session" or "End session"?
      </AppModal>
      }
      {hocProps.children}
    </HMSRoomProvider>
  );
}


const RecruiterPage: React.FC = (props) => {
  const { user } = useAuth();

  return (
    <Switch>
      <Route path={RecruiterRoutes.Booth} render={() => <RecruiterPageHOC><RecruiterBoothView title="Booth" /></RecruiterPageHOC>} />
      <Route path={RecruiterRoutes.Interview} render={() => <RecruiterPageHOC withContinuePopup={false}>
        <RecruiterInterview title="Interview" breadcrumbs={[...recruiterBreadCrumbs, { text: 'Interview' }]} />
      </RecruiterPageHOC>} />
      {/* <Route path={RecruiterRoutes.ManageBoothInfo} render={() => <RecruiterPageHOC withContinuePopup={false}> <ManageBoothInfo title="Manage Booth Information" breadcrumbs={manageBoothInfoBreadCrumbs} showMatrix={false} /> </RecruiterPageHOC>} /> */}

      <Route path={RecruiterRoutes.BoothPreview} render={() => <RecruiterPageHOC> {user?.organizationId && <JSBoothDetail orgID={user.organizationId} title="Booth Preview" breadcrumbs={[...recruiterBreadCrumbs, { text: 'Booth Preview' }]} />} </RecruiterPageHOC>} />
      <Route path={RecruiterRoutes.History} render={() => <RecruiterPageHOC> <RecruiterHistory title="Report" breadcrumbs={[...recruiterBreadCrumbs, { text: 'Report' }]} /></RecruiterPageHOC>} />
      <Route path={RecruiterRoutes.Profile} render={() => <RecruiterPageHOC><ManageProfile title="Profile" breadcrumbs={[...recruiterBreadCrumbs, { text: 'Profile' }]} /></RecruiterPageHOC>} />
      <Route path={RecruiterRoutes.ViewResume} render={() => <RecruiterPageHOC> <ViewResume /> </RecruiterPageHOC>} />
      <Redirect from={recruiterUrl} to={RecruiterRoutes.Booth} />
    </Switch>
  )
}

export default RecruiterPage
